import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';

import {
  FilterOptions,
  IFilterOptions,
} from '../components/Filter/Filter.types';
import { axiosClient } from '../config/axios-client';
import {
  NEW_TABLES_FIRST,
  VIEWING_TABLE_INSERTION_TIMEOUT,
} from '../constants';
import { ActiveTablesSortProperty } from '../model/ActiveTablesSorting';
import { GameType } from '../model/enumeration/GameType.enum';
import { SortDirection } from '../model/SortDirection';
import { TableAggregatedDataResponse } from '../model/TableAggregatedDataResponse';
import { TableProfit } from '../model/TableProfit';
import { TableResponse } from '../model/TableResponse';
import { TablesStats } from '../model/TablesStats';
import { TableUnion } from '../model/TableUnion';
import { waitUntilWithInterval } from '../utils/async.utils';
import { WEvent } from '../websocket/events/WEvent';
import botErrorStore from './BotErrorStore';
import chatStore from './ChatStore';
import commonStore from './CommonStore';
import gameStore from './GameStore';
import playerStore from './PlayerStore';
import sessionStorageStore from './SessionStorageStore';
import { GameTable } from './state/GameTable';
import { StateStore } from './StateStore';
import userSettingsStore from './UserSettingsStore';

const BASE_TABLES_URL = '/tables';

interface TableLock {
  id: string;
}

class TableStore implements StateStore {
  private tableLock: TableLock[] = [];

  public activeTables = new Map<string, GameTable>();
  public viewingModalWindowTableId: string | undefined;
  public viewingTableIds: string[] = [];
  public pinnedTableIds: string[] = [];

  public isActiveTablesInitialized = false;

  public trackedBotIds: string[] = [];

  public selectedFilterOptions: IFilterOptions = {} as IFilterOptions;

  public lastSelectedFilterOption: FilterOptions;

  public searchValueExpertMode: string;

  public defaultSelectedFilterOptions: IFilterOptions = {
    tableId: [],
    gameId: [],
    playerId: [],
    gameTypes: Object.keys(GameType).map(key => GameType[key]),
    onlyStartedGames: false,
    tableLimit: [],
    tableName: [],
    playerClub: [],
    playerLeague: [],
    playerUniqueId: [],
    sittingPlayers: [],
    sittingBots: [],
    sittingLivePlayers: [],
    onlyWithLivePlayers: false,
    onlyBots: false,
    autoOpenTables: false,
    autoCloseTables: false,
    hideFullTables: false,
  };

  public activeTablesSorting = new Map<
    ActiveTablesSortProperty,
    SortDirection
  >();

  public filterStateChanged = 0;

  public constructor() {
    makeAutoObservable(this);
    this.selectedFilterOptions =
      sessionStorageStore.getSelectedFilterOptions() ??
      this.defaultSelectedFilterOptions;
    this.pinnedTableIds = sessionStorageStore.getPinnedTableIds();
    this.activeTablesSorting = sessionStorageStore.getActiveTablesSorting();
    this.syncTrackedBots();
  }

  private syncTrackedBots = (): void => {
    const storedTrackedBotIds = sessionStorageStore.getTrackedBotIds();
    const trackedBotIds = this.selectedFilterOptions.autoOpenTables
      ? _.intersection(storedTrackedBotIds, this.selectedFilterOptions.playerId)
      : [];
    sessionStorageStore.setTrackedBotIds(trackedBotIds);
    this.trackedBotIds = trackedBotIds;
  };

  public resetState = (): void => {
    this.activeTables = new Map<string, GameTable>();
    this.viewingTableIds = [];
    this.viewingModalWindowTableId = undefined;
    this.tableLock = [];
    this.filterStateChanged = 0;
  };

  public getTableHistory = (tableId: string): Promise<WEvent[][]> =>
    axiosClient
      .get<WEvent[][]>(`${BASE_TABLES_URL}/${tableId}/history`)
      .then(response => response.data);

  public initializeTables = (
    viewingTableIds: string[],
    viewingModalTableId: string | undefined,
    onAllViewingTablesAdded: () => void,
  ): Promise<TableResponse[]> => {
    this.isActiveTablesInitialized = false;
    return axiosClient
      .get<TableResponse[]>(`${BASE_TABLES_URL}/active`, {
        params: { newTablesFirst: NEW_TABLES_FIRST },
      })
      .then(response => response.data)
      .then(tables => {
        runInAction(() => {
          this.setTablesFromResponse(tables);
          this.isActiveTablesInitialized = true;
        });
        return tables;
      })
      .then(tables => {
        this.openViewingTables(
          tables,
          viewingTableIds,
          viewingModalTableId,
          onAllViewingTablesAdded,
        );
        return tables;
      });
  };

  private openViewingTables = (
    tables: TableResponse[],
    viewingTableIds: string[],
    viewingModalTableId: string | undefined,
    onAllViewingTablesAdded: () => void,
  ): void => {
    if (
      viewingModalTableId &&
      tables.find(table => table.id === viewingModalTableId)
    ) {
      this.addViewingModalWindowTable(viewingModalTableId);
    }
    if (viewingTableIds) {
      const pinnedIdsSet = new Set(this.pinnedTableIds);
      const existingTableIds = viewingTableIds
        .filter(viewingTableId =>
          tables.find(table => table.id === viewingTableId),
        )
        .sort((first, second) => {
          return (
            (pinnedIdsSet.has(first) ? 0 : 1) -
            (pinnedIdsSet.has(second) ? 0 : 1)
          );
        });
      this.addViewingTableIds(existingTableIds, onAllViewingTablesAdded);
    } else {
      onAllViewingTablesAdded();
    }
  };

  public addViewingTableIds = async (
    tableIds: string[],
    onAllViewingTablesAdded: () => void,
  ): Promise<void> => {
    for (const [index, tableId] of tableIds.entries()) {
      if (!this.activeTables.size) {
        break;
      }
      if (index === 0 || index === 1) {
        this.addViewingTable(tableId);
      } else {
        await new Promise(resolve => {
          setTimeout(() => {
            if (this.activeTables.size) {
              this.addViewingTable(tableId);
              resolve(tableId);
            }
          }, VIEWING_TABLE_INSERTION_TIMEOUT);
        });
      }
    }
    onAllViewingTablesAdded();
  };

  public scrollToTable = (
    tableId: string,
    maxAttempts = 10,
    interval = 200,
  ): void => {
    this.removeViewingModalWindowTable();

    let currentAttempt = 0;
    const tryToFindAndScroll = (): void => {
      const table = document.querySelector<HTMLButtonElement>(
        `#table-${tableId}`,
      );
      if (table) {
        table.scrollIntoView({ block: 'center' });
      } else if (currentAttempt < maxAttempts) {
        currentAttempt++;
        setTimeout(tryToFindAndScroll, interval);
      }
    };
    tryToFindAndScroll();
  };

  public openAndScrollTable = (tableId: string, pid: string): void => {
    this.openAndScrollTableById(tableId, pid);
  };

  public openAndScrollTableById = (tableId: string, pid: string): void => {
    if (!this.isTableViewing(tableId)) {
      this.addViewingTable(tableId);
    }
    this.findActiveTableById(tableId)
      .then(table => {
        table.setGlowingPlayer(pid);
        this.scrollToTable(tableId);
      })
      .catch(() => null);
  };

  public pinTable = (tableId: string): void => {
    this.pinnedTableIds.push(tableId);
    if (!this.isTableViewing(tableId)) {
      this.addViewingTable(tableId);
    }
    sessionStorageStore.setPinnedTables(this.pinnedTableIds);
  };

  public unPinTable = (tableId: string): void => {
    const indexToRemove = this.pinnedTableIds.findIndex(id => id === tableId);
    if (indexToRemove > -1) {
      this.pinnedTableIds.splice(indexToRemove, 1);
      sessionStorageStore.setPinnedTables(this.pinnedTableIds);
    }
  };

  public isTablePinned = (tableId: string): boolean =>
    this.pinnedTableIds.some(id => id === tableId);

  public trackBot = (botId: string): void => {
    this.trackedBotIds.push(botId);
    sessionStorageStore.setTrackedBotIds(this.trackedBotIds);
    this.updateFilterOptions({
      ...this.defaultSelectedFilterOptions,
      playerId: [...this.trackedBotIds],
      autoOpenTables: true,
      hideFullTables: false,
      autoCloseTables: this.selectedFilterOptions.autoCloseTables,
    });
  };

  public stopTrackingBot = (botId: string, updateFilters = true): void => {
    const indexToRemove = this.trackedBotIds.findIndex(id => id === botId);
    if (indexToRemove > -1) {
      this.trackedBotIds.splice(indexToRemove, 1);
      sessionStorageStore.setTrackedBotIds(this.trackedBotIds);
      if (updateFilters) {
        this.updateFilterOptions({
          playerId: [...this.trackedBotIds],
          autoOpenTables: this.trackedBotIds.length > 0,
        });
      }
    }
  };

  public isBotTracked = (botId: string): boolean => {
    return this.trackedBotIds.some(id => id === botId);
  };

  public clearTables = (): void => {
    this.activeTables = new Map<string, GameTable>();
    this.viewingTableIds = [];
    this.viewingModalWindowTableId = undefined;
  };

  public updateFilterOptions = (options: FilterOptions): void => {
    this.selectedFilterOptions = { ...this.selectedFilterOptions, ...options };
    this.lastSelectedFilterOption = options;
    sessionStorageStore.setFilterOptions(this.selectedFilterOptions);
  };

  public getTableUniqueIds = (table: GameTable): string[] => {
    const uniqueIds = new Set(
      playerStore.findBots(table.players).map(player => player.uniqueId),
    );
    if (table.creatorUniqueId) {
      uniqueIds.add(table.creatorUniqueId);
    }
    table.waitingHostBots.forEach(bot => {
      if (bot.uniqueId) {
        uniqueIds.add(bot.uniqueId);
      }
    });
    return [...uniqueIds];
  };

  public getFilteredTablesExpertMode = (): GameTable[] => {
    const lowerSearchValue = this.searchValueExpertMode?.toLowerCase();

    if (!lowerSearchValue) return this.getFilteredTables();

    const result: GameTable[] = [];

    for (const table of this.activeTables.values()) {
      const hasMatchingPlayer = table.players.some(
        player =>
          player?.pid.toLowerCase().includes(lowerSearchValue) ||
          player?.nick.toLowerCase().includes(lowerSearchValue),
      );

      if (hasMatchingPlayer && this.isTableSatisfyFilter(table)) {
        result.push(table);
      }
    }

    return result;
  };

  public getFilteredTablesSorted = (): GameTable[] => {
    const filteredTables = commonStore.isExpertModeEnabled
      ? this.getFilteredTablesExpertMode()
      : this.getFilteredTables();

    if (this.activeTablesSorting.size === 0) {
      return filteredTables;
    }

    const sortOptions = [];
    const sortDirections = [];

    this.activeTablesSorting.forEach((direction, property) => {
      sortOptions.push(this.getSortOption(property));
      sortDirections.push(direction.toLowerCase());
    });

    return _.orderBy(filteredTables, sortOptions, sortDirections);
  };

  private getSortOption = (
    sortProperty: ActiveTablesSortProperty,
  ): ((table: GameTable) => number) => {
    switch (sortProperty) {
      case ActiveTablesSortProperty.LIMITS:
        return (table: GameTable) =>
          commonStore.isExpertModeEnabled ? table.bb : table.limit;
      case ActiveTablesSortProperty.LIVE_PLAYERS:
        return (table: GameTable) => playerStore.findLivePlayersCount(table);
      case ActiveTablesSortProperty.BOTS:
        return (table: GameTable) => playerStore.findBotsCount(table);
      case ActiveTablesSortProperty.OBSERVERS:
        return (table: GameTable) => table.viewersCount;
    }
  };

  public getFilteredTables = (): GameTable[] => {
    const result: GameTable[] = [];

    for (const table of this.activeTables.values()) {
      if (this.isTableSatisfyFilter(table)) {
        result.push(table);
      }
    }

    return result;
  };

  public isTableSatisfyFilter = (table: GameTable): boolean => {
    const {
      tableId,
      gameId,
      playerId,
      gameTypes,
      onlyStartedGames,
      tableLimit,
      tableName,
      playerClub,
      playerLeague,
      playerUniqueId,
      sittingPlayers,
      sittingBots,
      sittingLivePlayers,
      onlyWithLivePlayers,
      onlyBots,
      hideFullTables,
    } = this.selectedFilterOptions;
    const bots = playerStore.findBots(table.players);
    const players = playerStore.findPlayers(table);
    const livePlayersCount = playerStore.findLivePlayersCount(table);
    return (
      (hideFullTables ? table.players.length !== players.length : true) &&
      (onlyWithLivePlayers
        ? table.players.filter(player => player && !player.myBot).length
        : true) &&
      (onlyBots
        ? bots.length === players.length && players.length > 0
        : true) &&
      (sittingPlayers?.length
        ? sittingPlayers.includes(players.length)
        : true) &&
      (sittingBots?.length ? sittingBots.includes(bots.length) : true) &&
      (sittingLivePlayers?.length
        ? sittingLivePlayers.includes(livePlayersCount)
        : true) &&
      (tableLimit.length ? tableLimit.includes(table.limit) : true) &&
      (tableName.length ? tableName.includes(table.name) : true) &&
      (tableId.length ? tableId.includes(table.id) : true) &&
      (gameId.length ? gameId.includes(table.gameId) : true) &&
      (onlyStartedGames ? table.gameId : true) &&
      (playerId.length
        ? table.players.some(player => playerId.includes(player?.pid))
        : true) &&
      (playerClub.length
        ? table.players.some(player => playerClub.includes(player?.club)) ||
          table.waitingHostBots.some(bot => playerClub.includes(bot.club))
        : true) &&
      (playerLeague.length
        ? table.players.some(player => playerLeague.includes(player?.league))
        : true) &&
      (playerUniqueId.length
        ? table.players.some(
            player =>
              playerUniqueId.includes(player?.uniqueId) ||
              playerUniqueId.includes(table?.creatorUniqueId),
          ) ||
          table.waitingHostBots.some(bot =>
            playerUniqueId.includes(bot.uniqueId),
          )
        : true) &&
      gameTypes.includes(table.gameType)
    );
  };

  public setActiveTablesSortProperty = (
    sortProperty: ActiveTablesSortProperty,
    sortDirection: SortDirection,
  ): void => {
    this.activeTablesSorting = new Map([
      ...this.activeTablesSorting,
      [sortProperty, sortDirection],
    ]);
  };

  public deleteActiveTablesSortProperty = (
    sortProperty: ActiveTablesSortProperty,
  ): void => {
    this.activeTablesSorting.delete(sortProperty);
  };

  public getFilterOptions = (
    tables: IterableIterator<GameTable> | GameTable[],
  ): FilterOptions => {
    const tableIds = new Set<string>();
    const gameIds = new Set<string>();
    const playerIds = new Set<string>();
    const gameTypes = new Set<GameType>();
    const playerClubs = new Set<string>();
    const playerLeagues = new Set<string>();
    const playerUniqueIds = new Set<string>();
    const tableNames = new Set<string>();
    const tableLimits = new Set<number>();
    const sittingPlayers = new Set<number>();
    const sittingBots = new Set<number>();
    const sittingLivePlayers = new Set<number>();

    for (const table of tables) {
      if (table.id) tableIds.add(table.id);
      if (table.gameId) gameIds.add(table.gameId);
      if (table.gameType) gameTypes.add(table.gameType);
      if (table.name) tableNames.add(table.name);
      if (table.limit) tableLimits.add(commonStore.convertLimit(table.limit));

      const players = table.players ?? [];
      sittingPlayers.add(playerStore.findLivePlayersCount(table));
      sittingBots.add(playerStore.findBots(players).length);
      sittingLivePlayers.add(playerStore.findLivePlayersCount(table));

      for (const player of players) {
        if (!player) continue;
        if (player.pid) playerIds.add(player.pid);
        if (player.club) playerClubs.add(player.club);
        if (player.league) playerLeagues.add(player.league);
        if (player.uniqueId) playerUniqueIds.add(player.uniqueId);
      }

      for (const bot of table.waitingHostBots) {
        if (bot.club) {
          playerClubs.add(bot.club);
        }
        if (bot.uniqueId) {
          playerUniqueIds.add(bot.uniqueId);
        }
      }
    }

    return {
      tableId: Array.from(tableIds),
      gameId: Array.from(gameIds),
      playerId: Array.from(playerIds),
      gameTypes: Array.from(gameTypes),
      playerClub: Array.from(playerClubs),
      playerLeague: Array.from(playerLeagues),
      playerUniqueId: Array.from(playerUniqueIds),
      tableName: Array.from(tableNames),
      tableLimit: Array.from(tableLimits),
      sittingPlayers: Array.from(sittingPlayers),
      sittingBots: Array.from(sittingBots),
      sittingLivePlayers: Array.from(sittingLivePlayers),
    };
  };

  public getStaticFilterOptions = (): FilterOptions => {
    return this.getFilterOptions(this.activeTables.values());
  };

  public getDynamicFilterOptions = (): FilterOptions => {
    return this.getFilterOptions(this.getFilteredTables());
  };

  public resetSelectedFilters = (): void => {
    this.selectedFilterOptions = this.defaultSelectedFilterOptions;
  };

  public addViewingModalWindowTable = (tableId: string): void => {
    this.viewingModalWindowTableId = tableId;
  };

  public removeViewingModalWindowTable = (): void => {
    if (this.viewingModalWindowTableId) {
      this.clearTableDataIfDeleted(this.viewingModalWindowTableId);
    }
    this.viewingModalWindowTableId = undefined;
  };

  public addViewingTable = (tableId: string): void => {
    if (!this.viewingTableIds.includes(tableId)) {
      this.viewingTableIds.unshift(tableId);
    }
  };

  public setViewingTableIds = (viewingTableIds: string[]): void => {
    this.viewingTableIds = viewingTableIds;
  };

  public replaceViewingTable = (tableId: string): void => {
    if (this.viewingTableIds.length === 1) {
      const prevTableId = this.viewingTableIds[0];
      this.clearTableDataIfDeleted(prevTableId);
      gameStore.clearGameData(prevTableId);
      this.viewingTableIds = [tableId];
    }
  };

  public replaceViewingTableExpertMode = (tableId: string): void => {
    if (
      this.viewingTableIds.length === 1 &&
      this.viewingTableIds[0] !== tableId
    ) {
      const prevTableId = this.viewingTableIds[0];
      this.clearTableDataIfDeleted(prevTableId);
      gameStore.clearGameDataExcludeChatAndBotErrors(prevTableId);
      this.viewingTableIds = [tableId];
    }
  };

  public removeViewingTable = (tableId: string): void => {
    const indexToRemove = this.viewingTableIds.findIndex(id => id === tableId);
    if (indexToRemove > -1) {
      botErrorStore.removeToggleEvent(tableId);
      chatStore.removeToggleEvent(tableId);
      this.viewingTableIds.splice(indexToRemove, 1);
      gameStore.clearGameData(tableId);
      this.unPinTable(tableId);
      this.clearTableDataIfDeleted(tableId);
    }
  };

  public clearAllDeletedTables = (): void => {
    this.activeTables.forEach(table => this.clearTableDataIfDeleted(table.id));
  };

  private clearTableDataIfDeleted = (tableId: string): void => {
    const table = this.getActiveTableById(tableId);
    if (table?.deleted) {
      this.removeActiveTable(tableId);
    }
  };

  public setTables = (tables: GameTable[]): void => {
    this.activeTables = new Map(tables.map(table => [table.id, table]));
  };

  public setTablesFromResponse = (tables: TableResponse[]): void => {
    this.activeTables = new Map(
      tables.map(table => {
        const gameTable = new GameTable();
        gameTable.initTable(table);
        return [table.id, gameTable];
      }),
    );
  };

  public getActiveTableById = (tableId: string): GameTable =>
    this.activeTables.get(tableId);

  public findActiveTableById = async (tableId: string): Promise<GameTable> => {
    await waitUntilWithInterval(
      () => !this.tableLock.find(tableLock => tableLock.id === tableId),
    );
    this.tableLock.push({ id: tableId });

    return new Promise(resolve => {
      const table = this.getActiveTableById(tableId);
      table
        ? resolve(table)
        : this.loadActiveTable(tableId).then(response => resolve(response));
    }).finally(() => {
      const indexToRemove = this.tableLock.findIndex(
        tableLock => tableLock.id === tableId,
      );
      if (indexToRemove > -1) {
        this.tableLock.splice(indexToRemove, 1);
      }
    }) as Promise<GameTable>;
  };

  public getUnion = (tableId: string): TableUnion | undefined => {
    const table = this.getActiveTableById(tableId);
    return this.getUnionByTable(table);
  };

  public getUnionByTable = (table: GameTable): TableUnion | undefined => {
    if (table) {
      const unions = playerStore.findPlayers(table).map(
        player =>
          ({
            club: player.club,
            league: player.league,
            superLeague: player.superLeague,
          } as TableUnion),
      );

      const playerClubs = unions.map(union => union.club);

      const waitingHostBotClubs = table.waitingHostBots.map(bot => bot.club);

      const clubs = [...playerClubs, ...waitingHostBotClubs].filter(
        this.unionNotNullAndNotZeroValue,
      );

      const leagues = unions
        .map(union => union.league)
        .filter(this.unionNotNullAndNotZeroValue);

      const superLeagues = unions
        .map(union => union.superLeague)
        .filter(this.unionNotNullAndNotZeroValue);

      if (superLeagues.length) {
        return { superLeague: superLeagues[0] };
      } else if (leagues.length) {
        return { league: leagues[0] };
      } else if (clubs.length) {
        return { club: clubs[0] };
      }
    }
    return {};
  };

  public getLeaguesByTable = (table: GameTable): string[] => {
    if (table) {
      const leagues = playerStore
        .findPlayers(table)
        .map(player => player.league)
        .filter(this.unionNotNullAndNotZeroValue);
      return [...new Set(leagues)];
    }
    return [];
  };

  public getClubsByTable = (table: GameTable): string[] => {
    if (!table) return [];

    const playerClubs = playerStore
      .findPlayers(table)
      .map(player => player.club);

    const botClubs = table.waitingHostBots.map(bot => bot.club);

    const allClubs = [...playerClubs, ...botClubs].filter(
      this.unionNotNullAndNotZeroValue,
    );

    return [...new Set(allClubs)];
  };

  public unionNotNullAndNotZeroValue = (union: string): boolean =>
    union?.length > 0 && union !== '0';

  private loadActiveTable = (tableId: string): Promise<GameTable> =>
    axiosClient
      .get<TableResponse>(`${BASE_TABLES_URL}/${tableId}/active`)
      .then(response => response.data)
      .then(response => {
        const table = new GameTable();
        table.initTable(response);
        this.replaceTable(table);
        return table;
      });

  public loadTableAggregatedData = async (
    tableId: string,
    loadChatMessages: boolean,
    loadBotAiErrors: boolean,
  ): Promise<void> => {
    if (!loadChatMessages && !loadBotAiErrors) {
      return Promise.resolve();
    }
    return axiosClient
      .get<TableAggregatedDataResponse>(
        `${BASE_TABLES_URL}/${tableId}/active/aggregated`,
        {
          params: { loadChatMessages, loadBotAiErrors },
        },
      )
      .then(response => response.data)
      .then(response => {
        if (loadChatMessages) {
          chatStore.setTableChatMessages(tableId, response.chatMessages);
        }
        if (loadBotAiErrors) {
          botErrorStore.initBotErrors(tableId, response.botErrors);
        }
      })
      .catch(() => null);
  };

  public replaceTable = (newTable: GameTable): void => {
    this.activeTables.set(newTable.id, newTable);
  };

  public addTableIfNotExists = (newTable: GameTable): void => {
    if (this.activeTables.has(newTable.id)) return;

    if (NEW_TABLES_FIRST) {
      this.activeTables = new Map([
        [newTable.id, newTable],
        ...this.activeTables.entries(),
      ]);
    } else {
      this.activeTables.set(newTable.id, newTable);
    }
  };

  public deleteTable = (tableId: string): void => {
    this.removeAllBotsAndMarkDeleted(tableId);
    const isTableViewing =
      this.isTableViewing(tableId) ||
      this.viewingModalWindowTableId === tableId;
    setTimeout(
      () => {
        const table = this.getActiveTableById(tableId);
        if (table?.deleted) {
          this.clearTableData(tableId);
        }
        this.changeFilterState();
      },
      isTableViewing ? userSettingsStore.settings.table.autoCloseTables : 0,
    );
  };

  private clearTableData = (tableId: string): void => {
    if (this.viewingModalWindowTableId === tableId) {
      this.removeViewingModalWindowTable();
    }
    this.removeViewingTable(tableId);
    this.removeActiveTable(tableId);
  };

  private removeActiveTable = (tableId: string): void => {
    if (this.activeTables.delete(tableId)) {
      gameStore.clearGameData(tableId);
      this.changeFilterState();
    }
  };

  private removeAllBotsAndMarkDeleted = (tableId: string): void => {
    const table = this.getActiveTableById(tableId);
    if (table) {
      table.setDeleted(true);
      table.setPlayers(
        table.players.map(player => {
          if (!player || player.myBot) {
            return null;
          }
          return player;
        }),
      );
      table.clearWaitingHostBots();
    }
  };

  public isTableViewing = (tableId: string): boolean => {
    return this.viewingTableIds.includes(tableId);
  };

  public getStartedGamesCount = (tables: GameTable[]): number =>
    tables.filter(table => table.gameId).length;

  public changeFilterState = (): void => {
    this.filterStateChanged++;
  };

  public setIsActiveTablesInitialized = (
    isActiveTablesInitialized: boolean,
  ): void => {
    this.isActiveTablesInitialized = isActiveTablesInitialized;
  };

  public getTablesStats = (): TablesStats => {
    let tablesCount = 0;
    const livePids = new Set();
    const botPids = new Set();
    this.activeTables.forEach((table: GameTable) => {
      if (this.isTableSatisfyFilter(table)) {
        tablesCount++;
        table.players.forEach(player => {
          if (!player) return;
          if (player.myBot) {
            botPids.add(player.pid);
          } else {
            livePids.add(player.pid);
          }
        });
      }
    });
    return {
      livePlayersCount: livePids.size,
      botsCount: botPids.size,
      tablesCount: tablesCount,
    };
  };

  public loadTableProfit = async (tableId: string): Promise<TableProfit> => {
    return axiosClient
      .get<TableProfit>(`tables/${tableId}/profit`)
      .then(response => response.data);
  };

  public setSearchValueExpertMode = (value: string): void => {
    this.searchValueExpertMode = value;
  };
}

const tableStore = new TableStore();
export default tableStore;
